import { Controller } from '@hotwired/stimulus';
import { createModal, initModal } from '@superkoders/modal';
import { defaultOptions, defaultOptionsNoBgClick, wrapModalCloseAction } from './modal.js';

const parseHTMLFragment = (html) => {
	const template = document.createElement('template');
	template.innerHTML = html ?? '';
	return template.content;
};

const setHTML = (selector, html) => {
	document.querySelectorAll(selector).forEach((el) => {
		el.innerHTML = html ?? '';
	});
};

const replaceWithHTML = (selector, html) => {
	const nodes = Array.from(document.querySelectorAll(selector));
	if (nodes.length === 0) return;
	nodes.forEach((node, idx) => {
		const frag = parseHTMLFragment(html);
		node.replaceWith(idx === 0 ? frag : frag.cloneNode(true));
	});
};

const appendHTML = (selector, html) => {
	const frag = parseHTMLFragment(html);
	document.querySelectorAll(selector).forEach((el, idx) => {
		el.appendChild(idx === 0 ? frag : frag.cloneNode(true));
	});
};

const prependHTML = (selector, html) => {
	const frag = parseHTMLFragment(html);
	document.querySelectorAll(selector).forEach((el, idx) => {
		el.prepend(idx === 0 ? frag : frag.cloneNode(true));
	});
};

const serializeElement = (el) => {
	if (!el || !(el instanceof Element)) return '';
	const tag = el.tagName.toLowerCase();

	if (tag === 'form') {
		return new URLSearchParams(new FormData(el)).toString();
	}

	const name = el.getAttribute('name');
	if (!name) return '';

	if (el instanceof HTMLInputElement) {
		const type = (el.getAttribute('type') || '').toLowerCase();
		if ((type === 'checkbox' || type === 'radio') && !el.checked) return '';
		return new URLSearchParams([[name, el.value ?? '']]).toString();
	}

	if (el instanceof HTMLTextAreaElement) {
		return new URLSearchParams([[name, el.value ?? '']]).toString();
	}

	if (el instanceof HTMLSelectElement) {
		if (el.multiple) {
			const pairs = Array.from(el.selectedOptions).map((opt) => [name, opt.value]);
			return new URLSearchParams(pairs).toString();
		}
		return new URLSearchParams([[name, el.value ?? '']]).toString();
	}

	return '';
};

const QUERY_URL_OMIT_KEYS = new Set(['pageSize', 'language']);

const buildQueryUrl = (form, baseUrl) => {
    const entries = Array.from(new FormData(form).entries())
        .filter(([k, v]) => {
            if (QUERY_URL_OMIT_KEYS.has(k)) return false;
            if (v instanceof File) return false;
            const val = v.trim();
            if (val === '') return false;

            // Skip range inputs sitting at their boundary default (min for *From, max for *To)
            const el = form.elements.namedItem(k);
            if (el instanceof HTMLInputElement) {
                const key = k.toLowerCase();
                if (key.endsWith('from') && el.getAttribute('min') !== null && val === el.getAttribute('min')) return false;
                if (key.endsWith('to') && el.getAttribute('max') !== null && val === el.getAttribute('max')) return false;
            }

            return true;
        });
    const params = new URLSearchParams(entries).toString();
    const separator = baseUrl.includes('?') ? '&' : '?';
    return params ? `${baseUrl}${separator}${params}` : baseUrl;
};


const getDatasetBool = (el, key) => {
	const v = el?.dataset?.[key];
	if (v === undefined) return undefined;
	if (v === 'false') return false;
	if (v === 'true') return true;
	return v;
};

export const create = () => {
	return class extends Controller {
		connect() {
			this.body = document.body;
			this.lastState = false;
			this.weekly = false;

			this.AJAX_DEBOUNCE_DEFAULT_MS = 250;
			this.debounceTimers = new Map();
			this.ajaxFocusValues = new WeakMap();

			this.focusedNumberInput = null;
			this.doubleDot = false;

			this.onClick = this.onClick.bind(this);
			this.onChange = this.onChange.bind(this);
			this.onInput = this.onInput.bind(this);
			this.onFocus = this.onFocus.bind(this);
			this.onBlur = this.onBlur.bind(this);
			this.onSubmit = this.onSubmit.bind(this);
			this.onKeyPress = this.onKeyPress.bind(this);
			this.onPopState = this.onPopState.bind(this);
			this.onFocusInNumber = this.onFocusInNumber.bind(this);

			document.addEventListener('click', this.onClick);
			document.addEventListener('change', this.onChange);
			document.addEventListener('input', this.onInput);
			document.addEventListener('focus', this.onFocus, true);
			document.addEventListener('blur', this.onBlur, true);
			document.addEventListener('submit', this.onSubmit);
			document.addEventListener('keypress', this.onKeyPress);
			window.addEventListener('popstate', this.onPopState);
			document.addEventListener('focusin', this.onFocusInNumber);
		}

		disconnect() {
			document.removeEventListener('click', this.onClick);
			document.removeEventListener('change', this.onChange);
			document.removeEventListener('input', this.onInput);
			document.removeEventListener('focus', this.onFocus, true);
			document.removeEventListener('blur', this.onBlur, true);
			document.removeEventListener('submit', this.onSubmit);
			document.removeEventListener('keypress', this.onKeyPress);
			window.removeEventListener('popstate', this.onPopState);
			document.removeEventListener('focusin', this.onFocusInNumber);
		}

		actions = {
			html: (data) => {
				setHTML(data.targetBlock, data.content);

				setTimeout(() => {
					const activeSelectbox = document.querySelector('.js-selectbox.is-open');
					if (activeSelectbox) {
						activeSelectbox.querySelectorAll('.selectbox__label').forEach((label) => label.classList.add('is-filled'));
					}
				}, 0);
			},
			replaceWith: (data) => {
				replaceWithHTML(data.targetBlock, data.content);

				if (this.weekly === true) {
					document.querySelectorAll(data.targetBlock).forEach((el) => {
						el.classList.add('b-calendar--weekly');
						el.querySelectorAll('.js-calendar-view__toggle').forEach((t) => t.classList.remove('is-active'));
						const weekTab = el.querySelector('#tab-cal-week');
						if (weekTab) weekTab.classList.add('is-active');
					});
				}
			},
			append: (data) => {
				appendHTML(data.targetBlock, data.content);
			},
			prepend: (data) => {
				prependHTML(data.targetBlock, data.content);
			},
			fancybox: (data, triggeringElement = null) => {
				const isChoicesSelect = !!(triggeringElement && triggeringElement.closest('.choices'));
				const openModal = () => {
					document
						.querySelectorAll('.js-modal-close')
						.forEach((btn) => btn.dispatchEvent(new MouseEvent('click', { bubbles: true })));

					const item = [
						{
							url: data.content,
							medium: 'content',
						},
					];
					let modalClass = 'b-modal--content';

					if (data.modalClass) {
						modalClass = data.modalClass;
					} else if (triggeringElement && triggeringElement.dataset.modalClass) {
						modalClass = triggeringElement.dataset.modalClass;
					}

					const closeOnBgClick =
						data.closeOnBgClick !== undefined
							? data.closeOnBgClick
							: !(triggeringElement && triggeringElement.matches('[data-modal-no-bg-close]'));

					const customModal = createModal(item, {
						closeIcon: defaultOptions.closeIcon,
						closeOnBgClick,
						removeOnClose: true,
						customWrapperClass: modalClass,
						enableScrollLock: false,
						focusTrapOptions: defaultOptions.focusTrapOptions,
						onBeforeOpen: defaultOptions.onBeforeOpen,
						onModalOpen: defaultOptions.onModalOpen,
						onModalContentLoaded: defaultOptions.onModalContentLoaded,
					});
					wrapModalCloseAction(customModal);
					customModal.methods.open();
				};

				if (isChoicesSelect) {
					setTimeout(openModal, 100);
				} else {
					openModal();
				}
			},
			redirect: (data) => {
				window.location = data.url;
			},
			moveTo: (data) => {
				if (data.targetBlock?.length > 0) {
					window.location.hash = '#empty-space';
					window.location.hash = data.targetBlock;
				}
			},
		};

		buildResponse = (response, ajaxHistory, triggeringElement = null) => {
			response?.gtmevent?.forEach((event) => {
				let data = {};

				if (event.ecat) data.ecat = event.ecat;
				if (event.eact) data.event = event.eact;

				if (typeof event.elab === 'object') {
					Object.assign(data, event.elab);
				} else if (typeof event.elab === 'string') {
					const trimmedElab = event.elab.trim();
					if (trimmedElab.startsWith('{') || trimmedElab.startsWith('[')) {
						try {
							const elabObj = JSON.parse(event.elab);
							Object.assign(data, elabObj);
						} catch (error) {
							console.error('Chyba při parsování elab JSON stringu:', error);
							data.label = event.elab;
						}
					} else {
						data.label = event.elab;
					}
				} else {
					data.label = event.elab;
				}

				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push(data);
			});

			response?.data?.forEach((data) => {
				const hasTarget = data?.targetBlock ? document.querySelectorAll(data.targetBlock).length > 0 : false;
				if (hasTarget || data.type === 'fancybox' || data.type === 'redirect') {
					if (data.type === 'fancybox') {
						this.actions[data.type](data, triggeringElement);
					} else if (this.actions[data.type]) {
						this.actions[data.type](data);
					}
				}
			});

			setTimeout(() => {
				initModal(defaultOptions);
				initModal(defaultOptionsNoBgClick);
			}, 0);

			if (ajaxHistory && response?.url) {
				const state = {
					type: 'pageLoad',
					href: response.url,
					title: response.title,
				};

				history.pushState(state, '', response.url);
				this.changeState(state, true);
			}
		};

		changeState = (state, noAjax) => {
			if (!state) return;

			if (state.type === 'defaultDetail') {
				window.location.reload();
			} else if (state.type === 'pageLoad' && !noAjax) {
				this.callAjax(state.href, false);
			}

			if (state.title) {
				document.title = state.title;
			}

			this.lastState = state;
		};

		setLoading = (spinnerElement) => {
			this.body.classList.add('page-loading');

			if (spinnerElement === false) {
				return;
			}

			if (spinnerElement instanceof Element) {
				spinnerElement.classList.add('ajax-is-loading');
				const checkboxWrap = spinnerElement.closest('.inp-item--checkbox');
				if (checkboxWrap) checkboxWrap.classList.add('ajax-is-loading');
				return;
			}

			this.body.classList.add('page-loading-spinner');
		};

		clearLoading = (spinnerElement) => {
			this.body.classList.remove('page-loading', 'page-loading-spinner', 'page-loading-overlay');

			if (spinnerElement === false) return;
			if (!(spinnerElement instanceof Element)) return;

			spinnerElement.classList.remove('ajax-is-loading');
			const checkboxWrap = spinnerElement.closest('.inp-item--checkbox');
			if (checkboxWrap) checkboxWrap.classList.remove('ajax-is-loading');
		};

		maybeReAddLoadingOnRedirect = (response, spinnerElement) => {
			response?.data?.forEach((data) => {
				if (data.type !== 'redirect') return;
				this.setLoading(spinnerElement);
			});
		};

		scrollToPlaceholdersIfNeeded = (response) => {
			response?.data?.forEach((data) => {
				if (data?.targetBlock === '[data-placeholder="categoryFilter"]') {
					const el = document.querySelector('#category-products');
					if (el) el.scrollIntoView({ behavior: 'smooth' });
				}
				if (data?.targetBlock === '[data-placeholder="pagination"]') {
					const el = document.querySelector('#new-products');
					if (el) el.scrollIntoView({ behavior: 'smooth' });
				}
			});
		};

		callAjax = async (
			url,
			ajaxHistory,
			data,
			method,
			spinnerElement,
			callback = () => {},
			triggeringElement = null,
			useFormData = false,
		) => {
			this.setLoading(spinnerElement);

			let responseJson;
			try {
				const effectiveMethod = (method || 'GET').toUpperCase();
				const fetchOptions = {
					method: effectiveMethod,
					credentials: 'same-origin',
					headers: {
						Accept: 'application/json',
					},
				};

				let finalUrl = url;
				if (effectiveMethod === 'GET' || effectiveMethod === 'HEAD') {
					if (typeof data === 'string' && data.length > 0) {
						finalUrl += (finalUrl.includes('?') ? '&' : '?') + data;
					}
				} else if (useFormData && data instanceof FormData) {
					fetchOptions.body = data;
				} else if (typeof data === 'string') {
					fetchOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
					fetchOptions.body = data;
				} else if (data != null) {
					fetchOptions.headers['Content-Type'] = 'application/json; charset=UTF-8';
					fetchOptions.body = JSON.stringify(data);
				}

				const res = await fetch(finalUrl, fetchOptions);
				responseJson = await res.json();

				this.buildResponse(responseJson, ajaxHistory, triggeringElement);

				responseJson?.data?.forEach((item) => {
					if (item.closeModal === true) {
						document
							.querySelectorAll('.js-modal-close')
							.forEach((btn) => btn.dispatchEvent(new MouseEvent('click', { bubbles: true })));
					}
					if (item.fileName !== undefined) {
						const link = document.createElement('a');
						document.body.appendChild(link);
						link.setAttribute('type', 'hidden');
						link.href = 'data:text/plain;base64,' + item.fileBase64;
						link.download = item.fileName;
						link.click();
						document.body.removeChild(link);
					}
				});

				callback();

				if (triggeringElement instanceof Element) {
					const customEvent = triggeringElement.dataset.ajaxEvent;
					if (customEvent) {
						document.dispatchEvent(new Event(customEvent, { bubbles: true }));
					}
				}
			} finally {
				this.clearLoading(spinnerElement);
				this.maybeReAddLoadingOnRedirect(responseJson, spinnerElement);
				this.scrollToPlaceholdersIfNeeded(responseJson);
			}
		};

		syncEvents = (
			e,
			url,
			ajaxHistory,
			data,
			method,
			spinnerElement,
			callback = () => {},
			triggeringElement = null,
			useFormData = false,
		) => {
			e.preventDefault();

			if (this.body.classList.contains('page-loading')) {
				return;
			}

			if (ajaxHistory) {
				if (!this.lastState) {
					const state = {
						type: 'defaultDetail',
						href: window.location.href,
						title: document.title,
					};

					history.replaceState(state, '', window.location.href);
				}
			}

			this.callAjax(url, ajaxHistory, data, method, spinnerElement, callback, triggeringElement, useFormData);
		};

		getSpinnerElementFrom = (trigger) => {
			const raw = getDatasetBool(trigger, 'ajaxSpinner');
			if (raw === undefined) return undefined;
			if (raw === false) return false;

			const value = trigger.dataset.ajaxSpinner;
			if (value && value.length > 0) {
				const byId = document.getElementById(value);
				if (byId) return byId;
				this.body.classList.add('page-loading-spinner');
				return undefined;
			}

			return trigger;
		};

		onFocusInNumber(e) {
			const target = e.target;
			if (target instanceof Element && target.matches('.is-number')) {
				this.focusedNumberInput = target;
			}
		}

        onClick(e) {
            const target = e.target instanceof Element ? e.target : null;
            if (!target) return;

            // Handle facet badge removal buttons
            const removeBadgeButton = target.closest('button[data-ajax-remove-filter-name]');
            if (removeBadgeButton) {
                e.preventDefault();

                const formSelector = removeBadgeButton.dataset.ajaxForm;
                const form = formSelector
                    ? document.querySelector(formSelector)
                    : removeBadgeButton.closest('form');
                if (!(form instanceof HTMLFormElement)) return;

                const filterNames = (removeBadgeButton.dataset.ajaxRemoveFilterName || '')
                    .split(',')
                    .map((name) => name.trim())
                    .filter(Boolean);
                const filterValue = removeBadgeButton.dataset.ajaxRemoveFilterValue;
                if (filterNames.length === 0) return;

                const formControls = Array.from(form.elements).filter(
                    (el) => el instanceof Element && filterNames.includes(el.getAttribute('name')),
                );
                formControls.forEach((control) => {
                    if (!(control instanceof Element)) return;
                    const controlValue = 'value' in control ? control.value : control.getAttribute('value');
                    const matchesValue = filterValue === undefined || filterValue === '' || controlValue === filterValue;
                    if (!matchesValue) return;

                    if (control instanceof HTMLInputElement) {
                        const type = (control.getAttribute('type') || '').toLowerCase();
                        if (type === 'checkbox' || type === 'radio') {
                            control.checked = false;
                            control.dispatchEvent(new Event('change', { bubbles: true }));
                        } else {
                            control.value = '';
                            control.dispatchEvent(new Event('input', { bubbles: true }));
                        }
                        return;
                    }
                    if (control instanceof HTMLSelectElement) {
                        if (control.multiple) {
                            Array.from(control.options).forEach((opt) => {
                                if (filterValue === undefined || filterValue === '' || opt.value === filterValue)
                                    opt.selected = false;
                            });
                        } else {
                            control.value = '';
                        }
                        control.dispatchEvent(new Event('change', { bubbles: true }));
                        return;
                    }
                    if (control instanceof HTMLTextAreaElement) {
                        control.value = '';
                        control.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                });

                const url =
                    removeBadgeButton.getAttribute('data-ajax') ||
                    form.getAttribute('data-ajax-reset-submit') ||
                    form.getAttribute('data-ajax') ||
                    form.getAttribute('action');
                const ajaxHistory = form.dataset.ajaxHistory || false;
                const method = form.getAttribute('method');
                const spinnerElement = this.getSpinnerElementFrom(removeBadgeButton);
                const data = serializeElement(form);

                history.replaceState(null, '', buildQueryUrl(form, window.location.pathname));
                this.syncEvents(e, url, ajaxHistory, data, method, spinnerElement, () => { }, removeBadgeButton);
                return;
            }

			const ajaxEl = target.closest('a[data-ajax], button[data-ajax], span[data-ajax]');
			if (ajaxEl) {
                e.preventDefault();

                const dataAjax = ajaxEl.getAttribute('data-ajax');
                const pageParam = dataAjax
                    ? new URL(dataAjax, window.location.origin).searchParams.get('page')
                    : null;

                if (pageParam !== null) {
                    const params = new URLSearchParams(window.location.search);
                    pageParam === '1' ? params.delete('page') : params.set('page', pageParam);
                    const qs = params.toString();
                    history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
                }

                const url = dataAjax || ajaxEl.getAttribute('href');
                const spinnerElement = this.getSpinnerElementFrom(ajaxEl);
                const ajaxHistory = ajaxEl.dataset.ajaxHistory || false;

                this.weekly = !!ajaxEl.closest('.b-calendar--weekly');
                this.syncEvents(e, url, ajaxHistory, null, null, spinnerElement, () => { }, ajaxEl);
                return;
			}

			const submitBtn = target.closest('button[data-ajax-submit]');
			if (submitBtn) {
				e.preventDefault();

				const form = submitBtn.closest('form');
				if (!form) return;

				const url = submitBtn.getAttribute('data-ajax-submit') || form.getAttribute('action');
				const ajaxHistory = form.dataset.ajaxHistory || false;
				const method = form.getAttribute('method');

				let spinnerElement = submitBtn;
				let id = null;
				let cursorPosition = null;

				if (this.focusedNumberInput instanceof HTMLInputElement) {
					id = this.focusedNumberInput.getAttribute('id');
					cursorPosition = this.focusedNumberInput.selectionStart;
				}

				const numberInputs = Array.from(form.querySelectorAll('.is-number'));
				numberInputs.forEach((element) => {
					const parent = element.closest('.input');
					let value = (element.value ?? '').toString();
					value = value.replace(',', '.');
					element.value = value;

					if (isNaN(value) || value === '.' || value === ',' || value === '0') {
						parent?.classList.add('input--error');
					} else {
						parent?.classList.remove('input--error');
					}

					setTimeout(() => {
						const error = form.querySelector('.input--error');
						if (!error && value !== '') {
							const short = Math.round(parseFloat(value) * 100) / 100;
							if (!isNaN(short)) element.value = short;
						}
					}, 200);
				});

				setTimeout(() => {
					const error = form.querySelector('.input--error');
					if (error) return;

					const hasFileInputs = form.querySelectorAll('input[type="file"]').length > 0;
					const fileDropComponent = form.querySelector('[data-controller*="file-drop"]');
					const hasFileDrop = !!fileDropComponent;

					let payload;
					let useFormData = false;

					if (hasFileInputs || hasFileDrop) {
						useFormData = true;
						payload = new FormData(form);

						if (hasFileDrop) {
							const fileDropController = fileDropComponent.fileDropController;
							if (fileDropController && typeof fileDropController.getFiles === 'function') {
								const files = fileDropController.getFiles();
								payload.delete('files[]');
								payload.delete('files');
								files.forEach((file) => payload.append('files', file));
							}
						}

						if (submitBtn.getAttribute('name')) {
							payload.append(submitBtn.getAttribute('name'), submitBtn.getAttribute('value') || '');
						}

						if (submitBtn.dataset.ajaxIgnoreUfprt === 'true') {
							payload.delete('ufprt');
						}
					} else {
						payload = serializeElement(form);

						if (submitBtn.getAttribute('name')) {
							payload += (payload.length ? '&' : '') + submitBtn.getAttribute('name');
							if (submitBtn.getAttribute('value')) {
								payload += '=' + submitBtn.getAttribute('value');
							}
						}

						if (submitBtn.dataset.ajaxIgnoreUfprt === 'true') {
							payload = payload
								.split('&')
								.filter((x) => !x.startsWith('ufprt='))
								.join('&');
						}
					}

					this.syncEvents(
						e,
						url,
						ajaxHistory,
						payload,
						method,
						spinnerElement,
						() => {
							if (id) {
								const input = document.getElementById(id);
								if (input instanceof HTMLInputElement && cursorPosition != null) {
									input.setSelectionRange(cursorPosition, cursorPosition);
									input.focus();
								}
							}
						},
						submitBtn,
						useFormData,
					);
				}, 500);

				if (submitBtn.dataset.ajaxSpinner !== undefined) {
					const value = submitBtn.dataset.ajaxSpinner;
					if (value && value.length > 0) {
						const byId = document.getElementById(value);
						if (byId) {
							spinnerElement = byId;
						} else {
							this.body.classList.add('page-loading-spinner');
						}
					} else {
						const inpCount =
							submitBtn.closest('.inp-count') ||
							submitBtn.closest('form')?.querySelector('.inp-count') ||
							submitBtn.closest('.input');
						const label = inpCount?.querySelector('label');
						spinnerElement = label || inpCount || submitBtn.closest('.input') || submitBtn;
					}
				}

				if (submitBtn.dataset.modalClose === 'true') {
					document
						.querySelectorAll('.js-modal-close')
						.forEach((btn) => btn.dispatchEvent(new MouseEvent('click', { bubbles: true })));
				}

				return;
			}

			const anchorInAjaxDiv = target.closest('div[data-ajax] a');
			if (anchorInAjaxDiv && !(anchorInAjaxDiv instanceof Element && anchorInAjaxDiv.hasAttribute('data-ajax'))) {
				const closest = anchorInAjaxDiv.closest('div[data-ajax]');
				if (!closest) return;

				const baseUrl = (closest.getAttribute('data-ajax') || '').split('?')[0];
				const hrefBase = (anchorInAjaxDiv.getAttribute('href') || '').split('?')[0];
				const url = baseUrl || hrefBase;
				const ajaxHistory = anchorInAjaxDiv.dataset.ajaxHistory || false;
				let data = '';

				const divQuery = (closest.getAttribute('data-ajax') || '').split('?')[1];
				const hrefQuery = (anchorInAjaxDiv.getAttribute('href') || '').split('?')[1];
				if (divQuery !== undefined) data += divQuery;
				if (divQuery !== undefined && hrefQuery !== undefined) data += '&';
				if (hrefQuery !== undefined) data += hrefQuery;

				this.syncEvents(e, url, ajaxHistory, data, null, undefined, () => {}, anchorInAjaxDiv);
			}
		}

		onChange(e) {
			const target = e.target instanceof Element ? e.target : null;
			if (!target) return;

			const formAutoSubmit = target.closest('form[data-ajax-autosubmit]');
			const legacyAutoSubmit = target.matches('.is-checkbox-submit');

			// Preferred: opt-in on the form (submit whole form on change)
			// Back-compat: `.is-checkbox-submit` on an input
			if (
				formAutoSubmit &&
				target.matches('input, select, textarea') &&
				// ignore changes that come from submit buttons themselves
				!target.matches('button, input[type="submit"], input[type="button"], input[type="reset"]')
			) {
				const mode = formAutoSubmit.getAttribute('data-ajax-autosubmit');
				// default is "change"; allow "input" for text typing use-cases via onInput handler
				if (mode && mode.toLowerCase() === 'input') {
					return;
				}

				target.classList.add('is-checkbox-submit');
			}

			if (legacyAutoSubmit || (formAutoSubmit && target.classList.contains('is-checkbox-submit'))) {
				const inp = target;
				const form = inp.closest('form');
				if (!form) return;

                const url = form.getAttribute('data-ajax') || form.getAttribute('action');;
                // Sync URL bar with current filter state — no navigation, C# reads it on refresh/share
                history.replaceState(null, '', buildQueryUrl(form, window.location.pathname));
				const ajaxHistory = form.dataset.ajaxHistory || false;
				const method = form.getAttribute('method');
				const btns = Array.from(form.querySelectorAll('button[type="submit"], button:not([type])')).filter((b) =>
					b.matches('button:submit,button[type="submit"]'),
				);
				let spinnerElement;
				const id = inp.getAttribute('id');

				const numberInputs = Array.from(form.querySelectorAll('.is-number'));
				numberInputs.forEach((element) => {
					const parent = element.closest('.input');
					let value = (element.value ?? '').toString();
					value = value.replace(',', '.');
					element.value = value;

					if (isNaN(value) || value === '.' || value === ',' || value === '0') {
						parent?.classList.add('input--error');
					} else {
						parent?.classList.remove('input--error');
					}

					setTimeout(() => {
						const error = form.querySelector('.input--error');
						if (!error && value !== '') {
							const short = Math.round(parseFloat(value) * 100) / 100;
							if (!isNaN(short)) element.value = short;
						}
					}, 200);
				});

				setTimeout(() => {
					const error = form.querySelector('.input--error');
					if (error) return;

					let data = serializeElement(form);
					btns.forEach((b) => {
						if (b.getAttribute('name')) {
							data += (data.length ? '&' : '') + b.getAttribute('name');
							if (b.getAttribute('value')) {
								data += '=' + b.getAttribute('value');
							}
						}
					});

					this.syncEvents(
						e,
						url,
						ajaxHistory,
						data,
						method,
						spinnerElement,
						() => {
							if (id) {
								const inputElement = document.getElementById(id);
								const selectbox = inputElement?.closest?.('.js-selectbox');
								if (selectbox) selectbox.classList.add('is-open');
							}
						},
						form,
					);
				}, 500);

				if (form.dataset.ajaxSpinner !== undefined) {
					if (form.dataset.ajaxSpinner === 'false') {
						spinnerElement = false;
					} else if (form.dataset.ajaxSpinner.length > 0) {
						const byId = document.getElementById(form.dataset.ajaxSpinner);
						if (byId) {
							spinnerElement = byId;
						} else {
							this.body.classList.add('page-loading-spinner');
						}
					} else {
						spinnerElement = form;
					}
				}

				return;
			}

			if (!target.matches('input[data-ajax], select[data-ajax], textarea[data-ajax]')) return;

			const pickerContainer = document.querySelector('.timepicker-container');
			if (pickerContainer && target.closest('[data-controller*="timepicker"]')) {
				return;
			}

			if ((target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') && target.dataset.ajaxTimeout !== undefined) {
				return;
			}

			const url = target.getAttribute('data-ajax');
			const ajaxHistory = target.dataset.ajaxHistory || false;
			let data = serializeElement(target);
			let spinnerElement;

			const isChoicesSelect = !!target.closest('.choices');
			if (isChoicesSelect) e.stopPropagation();

			if (target.dataset.ajaxSpinner !== undefined) {
				if (target.dataset.ajaxSpinner === 'false') {
					spinnerElement = false;
				} else if (target.dataset.ajaxSpinner.length > 0) {
					const byId = document.getElementById(target.dataset.ajaxSpinner);
					if (byId) spinnerElement = byId;
					else this.body.classList.add('page-loading-spinner');
				} else {
					spinnerElement = target.parentElement;
				}
			}

			if (target instanceof HTMLSelectElement) {
				const selected = target.selectedOptions?.[0];
				const text = selected ? selected.textContent.trim() : '';
				data = 'selvalue=' + encodeURIComponent(target.value) + '&seltext=' + encodeURIComponent(text);
			}

			this.syncEvents(e, url, ajaxHistory, data, null, spinnerElement, () => {}, target);
		}

		onInput(e) {
			const target = e.target instanceof Element ? e.target : null;
			if (!target) return;

			// Optional: submit whole form while typing (opt-in on the form)
			const formAutoSubmit = target.closest('form[data-ajax-autosubmit="input"]');
			if (formAutoSubmit && target.matches('input, textarea')) {
				// keep behavior consistent with onChange path
				target.classList.add('is-checkbox-submit');
				// trigger the same form-submit logic (debounced via data-ajax-timeout if desired)
				// here we debounce using existing data-ajax-timeout on the input if present
				const timeoutMs =
					target.dataset.ajaxTimeout !== undefined
						? parseInt(target.dataset.ajaxTimeout, 10) || this.AJAX_DEBOUNCE_DEFAULT_MS
						: this.AJAX_DEBOUNCE_DEFAULT_MS;

				if (this.debounceTimers.has(target)) {
					clearTimeout(this.debounceTimers.get(target));
				}

				this.debounceTimers.set(
					target,
					setTimeout(() => {
						this.debounceTimers.delete(target);
						this.onChange(e);
					}, timeoutMs),
				);
				return;
			}

			if (target.matches('.is-number') && target instanceof HTMLInputElement) {
				const inputValue = (target.value ?? '').toString().replace(',', '.');
				const cursorPosition = target.selectionStart ?? inputValue.length;
				let newValue = '';
				let dotSeen = false;
				let removedBeforeCursor = 0;

				for (let i = 0; i < inputValue.length; i++) {
					const char = inputValue.charAt(i);
					const keepDigit = char >= '0' && char <= '9';
					const keepDot = char === '.' && !dotSeen;

					if (keepDigit) {
						newValue += char;
						continue;
					}

					if (keepDot) {
						newValue += char;
						dotSeen = true;
						continue;
					}

					if (i < cursorPosition) removedBeforeCursor++;
				}

				target.value = newValue;
				const newCursorPosition = Math.max(0, cursorPosition - removedBeforeCursor);
				target.setSelectionRange(newCursorPosition, newCursorPosition);
			}

			if (!target.matches('input[data-ajax][data-ajax-timeout], textarea[data-ajax][data-ajax-timeout]')) return;

			const el = target;
			const timeoutMs = parseInt(target.dataset.ajaxTimeout, 10) || this.AJAX_DEBOUNCE_DEFAULT_MS;

			if (this.debounceTimers.has(el)) {
				clearTimeout(this.debounceTimers.get(el));
			}

			this.debounceTimers.set(
				el,
				setTimeout(() => {
					this.debounceTimers.delete(el);
					const url = target.getAttribute('data-ajax');
					const ajaxHistory = target.dataset.ajaxHistory || false;
					const data = serializeElement(target);
					let spinnerElement;

					if (target.dataset.ajaxSpinner !== undefined) {
						if (target.dataset.ajaxSpinner === 'false') {
							spinnerElement = false;
						} else if (target.dataset.ajaxSpinner.length > 0) {
							const byId = document.getElementById(target.dataset.ajaxSpinner);
							if (byId) spinnerElement = byId;
							else this.body.classList.add('page-loading-spinner');
						} else {
							spinnerElement = target.parentElement;
						}
					}

					this.syncEvents(e, url, ajaxHistory, data, null, spinnerElement, () => {}, target);
				}, timeoutMs),
			);
		}

		onFocus(e) {
			const target = e.target instanceof Element ? e.target : null;
			if (!target) return;

			if (target.matches('input[data-ajax][data-ajax-timeout], textarea[data-ajax][data-ajax-timeout]')) {
				this.ajaxFocusValues.set(target, target.value);
			}
		}

		onBlur(e) {
			const target = e.target instanceof Element ? e.target : null;
			if (!target) return;

			if (!target.matches('input[data-ajax][data-ajax-timeout], textarea[data-ajax][data-ajax-timeout]')) return;

			if (this.debounceTimers.has(target)) {
				clearTimeout(this.debounceTimers.get(target));
				this.debounceTimers.delete(target);
			}

			const valueOnFocus = this.ajaxFocusValues.get(target);
			if (valueOnFocus !== undefined && valueOnFocus === target.value) {
				return;
			}

			const url = target.getAttribute('data-ajax');
			const ajaxHistory = target.dataset.ajaxHistory || false;
			const data = serializeElement(target);
			let spinnerElement;

			if (target.dataset.ajaxSpinner !== undefined) {
				if (target.dataset.ajaxSpinner === 'false') {
					spinnerElement = false;
				} else if (target.dataset.ajaxSpinner.length > 0) {
					const byId = document.getElementById(target.dataset.ajaxSpinner);
					if (byId) spinnerElement = byId;
					else this.body.classList.add('page-loading-spinner');
				} else {
					spinnerElement = target.parentElement;
				}
			}

			this.syncEvents(e, url, ajaxHistory, data, null, spinnerElement, () => {}, target);
		}

		onSubmit(e) {
			const target = e.target instanceof Element ? e.target : null;
			if (!target) return;
			if (!target.matches('form[data-ajax]:not([data-ajax-mode])')) return;

            const form = target;

            // Sync URL bar on explicit submit
            history.replaceState(null, '', buildQueryUrl(form, window.location.pathname));

			const url = form.getAttribute('data-ajax') || form.getAttribute('action');
			const ajaxHistory = form.dataset.ajaxHistory || false;
			let data = serializeElement(form);
			const method = form.getAttribute('method');
			const btns = Array.from(form.querySelectorAll('button[type="submit"], button:not([type])'));
			let spinnerElement;

			if (form.dataset.ajaxSpinner !== undefined) {
				if (form.dataset.ajaxSpinner === 'false') {
					spinnerElement = false;
				} else if (form.dataset.ajaxSpinner.length > 0) {
					const byId = document.getElementById(form.dataset.ajaxSpinner);
					if (byId) spinnerElement = byId;
					else this.body.classList.add('page-loading-spinner');
				} else {
					spinnerElement = form;
				}
			}

			btns.forEach((b) => {
				if (b.getAttribute('name')) {
					data += (data.length ? '&' : '') + b.getAttribute('name');
					if (b.getAttribute('value')) {
						data += '=' + b.getAttribute('value');
					}
				}
			});

			const ignoreUfprt = form.dataset.ajaxIgnoreUfprt === 'true' || btns.some((b) => b.dataset.ajaxIgnoreUfprt === 'true');
			if (ignoreUfprt) {
				data = data
					.split('&')
					.filter((x) => !x.startsWith('ufprt='))
					.join('&');
			}

			this.syncEvents(e, url, ajaxHistory, data, method, spinnerElement, () => {}, form);
		}

		onKeyPress(e) {
			const target = e.target instanceof Element ? e.target : null;
			if (!target) return;

			if (target.matches('input[data-ajax]') && e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				target.blur();
				return;
			}

			if (target.matches('.is-stop-submit') && e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				return;
			}

			if (target.matches('.is-enter-submit') && e.key === 'Enter') {
				e.preventDefault();

				const btn = target;
				const form = btn.closest('form');
				if (!form) return;

				const url = btn.getAttribute('data-ajax-submit') || form.getAttribute('action');
				const ajaxHistory = form.dataset.ajaxHistory || false;
				const method = form.getAttribute('method');
				let spinnerElement;
				const id = btn.getAttribute('id');
				const cursorPosition = btn instanceof HTMLInputElement ? btn.selectionStart : null;

				const numberInputs = Array.from(form.querySelectorAll('.is-number'));
				numberInputs.forEach((element) => {
					const parent = element.closest('.input');
					let value = (element.value ?? '').toString();
					value = value.replace(',', '.');
					element.value = value;

					if (isNaN(value) || value === '.' || value === ',' || value === '0') {
						parent?.classList.add('input--error');
					} else {
						parent?.classList.remove('input--error');
					}

					setTimeout(() => {
						const error = form.querySelector('.input--error');
						if (!error && value !== '') {
							const short = Math.round(parseFloat(value) * 100) / 100;
							if (!isNaN(short)) element.value = short;
						}
					}, 200);
				});

				setTimeout(() => {
					const error = form.querySelector('.input--error');
					if (error) return;

					let data = serializeElement(form);
					if (btn.getAttribute('name')) {
						data += (data.length ? '&' : '') + btn.getAttribute('name');
						if (btn.getAttribute('value')) {
							data += '=' + btn.getAttribute('value');
						}
					}

					this.syncEvents(
						e,
						url,
						ajaxHistory,
						data,
						method,
						spinnerElement,
						() => {
							if (id) {
								const input = document.getElementById(id);
								if (input instanceof HTMLInputElement && !input.classList.contains('is-basket') && cursorPosition != null) {
									input.setSelectionRange(cursorPosition, cursorPosition);
									input.focus();
								}
							}
						},
						form,
					);
				}, 500);

				if (btn.dataset.ajaxSpinner !== undefined) {
					if (btn.dataset.ajaxSpinner.length > 0) {
						const byId = document.getElementById(btn.dataset.ajaxSpinner);
						if (byId) spinnerElement = byId;
						else this.body.classList.add('page-loading-spinner');
					} else {
						spinnerElement = btn.closest('.input');
					}
				}

				if (btn.dataset.modalClose === 'true') {
					document
						.querySelectorAll('.js-modal-close')
						.forEach((x) => x.dispatchEvent(new MouseEvent('click', { bubbles: true })));
				}
			}
		}

		onPopState(e) {
			this.changeState(e.state);
		}
	};
};
